import { showDeleteConfirmation } from '@modules/common';
import { map, switchMap, tap } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import { FolderId, FolderResponse, FolderStructureResponse, SearchResult } from '@business/entities/common/FolderResponse';
import { FolderAPI } from '@business/api/folder_api';
import { BehaviorSubject, Observable } from 'rxjs';

export class FolderBloc extends BaseBloc {
  private readonly foldersRepo = new Repository<FolderResponse>({
    getItemId: folder => folder.id,
  });

  private readonly _breadcrumbs$ = new BehaviorSubject<{ id: string; name: string; object_type?: string }[]>([]);

  readonly folders$ = this.foldersRepo.items$;
  readonly selectFolder = this.foldersRepo.selectItem;
  readonly breadcrumbs$ = this._breadcrumbs$.asObservable();

  onReset() {
    this.foldersRepo.reset();
    this._breadcrumbs$.next([]);
  }

  /**
   * Fetch folders with optional parent filtering
   */
  fetchFolders = (objectType: string, parentId?: FolderId) => {
    return FolderAPI.fetchFolders(objectType, parentId).pipe(
      this.foldersRepo.ops.upsertMany(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_folder') }),
    );
  };

  /**
   * Fetch a specific folder by ID
   */
  fetchFolder = (id: FolderId) => {
    return FolderAPI.fetchFolder(id).pipe(
      tap(folder => {
        // Update breadcrumbs when fetching a folder
        if (folder.breadcrumbs) {
          this._breadcrumbs$.next(folder.breadcrumbs);
        }
      }),
      this.foldersRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_folder') }),
    );
  };

  /**
   * Create a new folder
   */
  createFolder = (data: Partial<FolderResponse> & { objectType: string }) => {
    return FolderAPI.createFolder(data).pipe(
      this.foldersRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_folder'),
      }),
    );
  };

  /**
   * Update an existing folder
   */
  updateFolder = (id: FolderId, data: Partial<FolderResponse>) => {
    return FolderAPI.updateFolder(id, data).pipe(
      this.foldersRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_folder'),
      }),
    );
  };

  /**
   * Delete a folder with confirmation
   */
  deleteFolder = (id: FolderId, moveToParent: boolean = false, parent?: FolderResponse) => {
    const confirmationMessage = moveToParent
      ? `Do you really want to remove this folder? All contents will be moved to ${parent ? `"${parent.name}"` : ' the Root folder'}.`
      : 'Do you really want to remove this folder? All contents will be deleted.';

    return showDeleteConfirmation('Delete Folder', confirmationMessage).pipe(
      switchMap(() => FolderAPI.deleteFolder(id, moveToParent)),
      this.foldersRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_folder'),
      }),
    );
  };

  /**
   * Get folder structure for organizing
   */
  getFolderStructure = (objectType: string, folderId?: FolderId): Observable<FolderStructureResponse> => {
    return FolderAPI.folderStructure(objectType, folderId).pipe(handleMessage({ error: requestMessage('fetch_folder_structure') }));
  };

  /**
   * Get breadcrumbs for a folder
   */
  getBreadcrumbs = (id: FolderId) => {
    return FolderAPI.getBreadcrumbs(id).pipe(
      tap(response => {
        this._breadcrumbs$.next(response.breadcrumbs);
      }),
      handleMessage({ error: requestMessage('fetch_breadcrumbs') }),
    );
  };

  /**
   * Move an item to a different folder
   */
  moveItem = (itemId: string, objectType: string, toFolderId: string) => {
    return FolderAPI.moveItem({
      itemId,
      objectType,
      toFolderId,
    }).pipe(
      handleMessage({
        type: requestMessage('move_item'),
      }),
    );
  };

  /**
   * Move a folder to a different parent
   */
  moveFolder = (folderId: string, toParentId: string) => {
    return FolderAPI.moveFolder({
      folderId,
      toParentId,
    }).pipe(
      handleMessage({
        type: requestMessage('move_folder'),
      }),
    );
  };

  /**
   * Search items and folders
   */
  searchItems = (query: string, objectType: string): Observable<SearchResult[]> => {
    return FolderAPI.searchItems(query, objectType).pipe(handleMessage({ error: requestMessage('search_items') }));
  };
}
